import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { loadArtworkTexture } from '../artwork-texture.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';

// Shared-rig contract (see the framing addendum). 'forbidden': every room here
// carries its own light, and the whole point of the piece is passing from one
// light into the next. A rig lighting all four rooms the same way would erase
// exactly that.
const RIG = 'forbidden';
export { RIG };

// Orchestrator registry entry (see visualizer-registry.js).
export const PROFILE = {
  id: 'portals',
  family: 'object',
  danceability: 0.68,
  energyLevel: 0.65,
  bpmMin: 95,
  bpmMax: 165,
  movement: 0.70,
  tonality: 0.80,
  prefers: { gridStrength: [0.50, 1.0] },
  enabled: true
};

// Portals: an endless journey through a succession of rooms, each a different
// setting derived from the cover, joined by openings you pass through.
// Electronic, downtempo, deep house, atmospheric pop.
//
// DrosteDive zooms INTO an image. This one TRAVERSES spaces: you advance through
// a room, an opening in its far wall frames the next room, you pass through it,
// and it starts again, forever. A corridor of doors onto places that are all
// different, never arriving anywhere.
//
// WHAT MAKES IT ENDLESS: a ring of four rooms. The camera runs down -Z without
// stopping; rooms sit 14 units apart, each with its opening in the far wall.
// When the camera has passed through a room, that room is moved 4 x 14 units
// ahead and REGENERATED with a new setting - at that distance the fog has
// already dissolved it entirely, so the regeneration is never seen. Two or
// three rooms are visible at any moment, nested in depth, and nothing is ever
// allocated: a room is a fixed set of five walls whose parameters change.
//
// WHAT MAKES IT A JOURNEY RATHER THAN A TUNNEL - three things, none optional:
//   a. Each room has its OWN light, in colour and strength, and the next
//      room's light already licks the edge of the opening before you reach it.
//      That foretaste is what gives the depth.
//   b. The openings are OFF-CENTRE, and the camera steers smoothly to pass
//      through each one, so the path serpentines instead of running straight.
//   c. The walls are OPAQUE and depth-written, so a room really occludes the
//      ones behind it - real depth doing the work, not a fade.
//
// THE CROSSING IS THE EVENT, and it lands on the music. Travel is measured in
// BEATS, not seconds, taken from the beat grid's own index + phase, so a
// crossing falls every eight beats exactly whenever there is a bpm. On a known
// drop the travel doubles for one bar.

// --- the ring ----------------------------------------------------------------
const RING = 4;                 // rooms in the ring (the layout may use fewer)
const SPACING = 14.0;           // opening to opening, in world units
const ROOM_SIZE = 12.0;         // the cube, and the width of every wall
const DOOR_DEPTH = SPACING - ROOM_SIZE;   // the 2-unit threshold past each opening
const BEATS_PER_ROOM = 8;
// The passage is a framed artwork whose CONTENT is exactly the size of the DOM
// cover at the moment the cover lands on it. That is a real geometric
// constraint rather than a look: the aperture's half-height is SOLVED from the
// measured artwork element and the distance at which the landing happens, so
// the match stays exact at any resolution.
//
// It also forces the aperture to be COVER-shaped. A circular or hexagonal
// opening would let a square cover's corners overhang the moulding, which
// reads as a mistake - so the five aperture outlines collapsed into one
// rectangle at the cover's own aspect, and the per-room variety moved to the
// moulding instead (its width, which is more apt for a gallery anyway).
// As close to the crossing as the geometry allows. A fixed-size picture
// matches a fixed-size screen rect at exactly ONE distance, which can never be
// zero - so the contact cannot literally BE the crossing, and the honest
// answer is to put it a beat and a half before, where it reads as one event
// with the passage: superpose, flash, through.
const LANDING_BEATS = 1.5;      // before the crossing, the covers superpose
const LAND_PHASE = 1 - LANDING_BEATS / BEATS_PER_ROOM;   // where in the room that is
// The wander has to be back to nothing a clear beat BEFORE the contact, not
// exactly at it: ending it on the contact itself left the camera still
// arriving at the axis when the two covers met, and the residual threw the
// frame up to a sixth of the cover sideways.
const SWAY_END_PHASE = 1 - (LANDING_BEATS + 1.0) / BEATS_PER_ROOM;
// And it has to START late enough that the LOOKAHEAD, which at the contact is
// already reading into the NEXT room, finds that room's wander still at zero.
// This was the real residual: not the camera's own position, which was exactly
// on the axis, but where it was looking - two beats ahead, into a room that had
// begun to weave. The wander now lives in the middle of each room and both
// thresholds are approached and left dead straight.
const SWAY_START_PHASE = 0.18;
const LAND_DECAY = 5.5;         // the landing flare's fall, per second
// The moulding is a FRACTION of the aperture, not an absolute width: the
// aperture is now solved from the cover and is small in world units, so a
// fixed 0.8 would have been wider than the picture it framed.
const MOULD_FRAC = 0.30;        // of the aperture half-size
// The canvas hanging in the frame: the cover, as a veil rather than a wall.
// Opaque would hide the rooms beyond and kill the nesting; this lets the next
// room glow through the painting, and the painting dissolves in the last beats
// so the crossing is a step INTO the picture.
// Near-opaque, because the whole point is that the print and the fixed cover
// merge into one picture at the contact. At 0.62 the rim that appears just
// after the contact read as a ghost beside the opaque DOM cover instead of as
// a continuation of it.
const CANVAS_PEAK = 0.88;
const CANVAS_QUAD = 3.0;        // quad width as a multiple of the aperture half-size
// Wider than it was: the frame now registers with the cover, so whenever it is
// smaller than the cover on screen it hides BEHIND it. The wander is what
// holds it clear of the cover during the approach, so there is a picture to
// watch coming rather than one that only appears at the last moment.
const OFFSET_MIN = 0.09, OFFSET_MAX = 0.22;   // of the wall, both axes

// --- fog ---------------------------------------------------------------------
// exp2 at 0.04: a room one step away keeps 73% of itself, two steps 29%, three
// steps 6%. Three rooms visible, the fourth already gone - which is exactly what
// lets the ring regenerate that fourth room unseen.
const FOG_DENSITY = 0.04;

// --- reactivity ----------------------------------------------------------------
const FALLBACK_BPM = 120;
const LIGHT_PULSE = 0.25;       // beatPhase -> the current room's light
const FRAME_ANTICIPATE = 2;     // beats before the crossing the frame lights up
// How far down the path the camera aims. Comfortably more than one room, so
// the direction is dominated by -Z with only a gentle lateral component, and
// so the aim never sits on a segment joint at the same moment the camera does.
// Short on purpose. The aim is a point further down the same path, and the
// path is dead straight from the landing to the crossing, so a SHORT lookahead
// keeps the aim inside that straight stretch - which is what holds the frame
// centred on screen while the cover sits in it. A long one reached into the
// next room's sway and pulled the frame off centre exactly when it had to be
// still. It swings to the new room's wander just after you are through.
const LOOKAHEAD_BEATS = 2;
const DROP_MULT = 2;            // travel speed on a known drop, for one bar
const DROP_SCORE_MIN = 0.6;

const SAFE_FLOOR = 0.5;

// Setting types. The orchestrator of the ring keeps two consecutive rooms from
// sharing one, and never lets two MIRROR rooms exist at once (the one that is
// costly). VOID is the breath of the cycle: about one room in five.
const TYPES = ['gallery', 'mirror', 'lattice', 'drape', 'void', 'tile'];
const TYPE_ID = { gallery: 0, mirror: 1, lattice: 2, drape: 3, void: 4, tile: 5 };
const SHAPES = 5;               // rectangle, arch, circle, ogive, hexagon

const TIER = {
  0: { rooms: 3, types: ['gallery', 'lattice', 'void'], fog: true, simpleLight: true },
  1: { rooms: 3, types: ['gallery', 'lattice', 'void', 'tile'], fog: true, simpleLight: false },
  2: { rooms: 4, types: TYPES, fog: true, simpleLight: false }
};
const DEFAULT_TIER = 2;

const FALLBACK_VIVID = [0x7fd0ff, 0xff6fd0, 0xffd66a, 0x6effc8, 0x9a80ff, 0xff9f5a];

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function lerp(a, b, t) { return a + (b - a) * t; }
function urlParam(n) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(n);
}

// ---------------------------------------------------------------------------
// The wall shader. One material per wall, five per room; `uWall` says which
// face this is and `uType` which setting to draw. Everything a setting needs is
// procedural in here, off the same cover texture, so regenerating a room is a
// handful of uniform writes and nothing else.
//
// Walls are in ROOM-LOCAL space: x,y in [-6,6], z from 0 (the threshold) down
// to -12 (the far wall). The far wall is wall 4 and carries the opening.
// ---------------------------------------------------------------------------
// The opening's outline, shared by the wall that is cut by it and by the
// CANVAS that hangs in it, so the painting can never sit outside its own
// frame. Centred on the origin; each caller passes its own local coordinates
// and the half-extents as a vec2, which is what carries the cover's aspect.
const OPENING_SDF_CHUNK = `
  float openingShape(vec2 q, vec2 h) {
    vec2 d = abs(q) - h;
    return length(max(d, 0.0)) + min(max(d.x, d.y), 0.0);
  }
`;

const wallVertexShader = `
  precision mediump float;
  varying vec2 vUv;
  varying vec3 vLocal;     // room-local position
  varying vec3 vWorld;
  varying vec4 vClip;
  void main() {
    vUv = uv;
    vLocal = position;
    vec4 w = modelMatrix * vec4(position, 1.0);
    vWorld = w.xyz;
    vClip = projectionMatrix * viewMatrix * w;
    gl_Position = vClip;
  }
`;

const wallFragmentShader = `
  precision mediump float;

  uniform float uWall;         // 0 left, 1 right, 2 floor, 3 ceiling, 4 far
  uniform float uType;         // TYPE_ID
  uniform float uShape;        // room variant; drives uMouldW on the JS side
  uniform vec2 uOpenCentre;    // opening centre on the far wall, room-local xy
  uniform float uOpenHalf;     // opening half-HEIGHT, room-local
  uniform float uOpenAspect;   // the cover's width/height
  uniform float uMouldW;       // this room's moulding width
  uniform vec3 uTint;          // this room's dominant colour
  uniform vec3 uTint2;         // its secondary
  uniform vec3 uLightCol;      // this room's light
  uniform vec3 uLightPos;      // room-local
  uniform float uLightAmt;     // 0..~1.6 - low band + beat pulse
  uniform vec3 uNextLight;     // the NEXT room's light, licking the opening
  uniform float uFrameGlow;    // 0..1 - the opening frame lighting up
  uniform float uHigh;         // bands.high - reflections, grids, tiles
  uniform float uSeed;
  uniform float uTime;
  uniform sampler2D uArt;
  uniform float uHasArt;
  uniform vec3 uFogColor;
  uniform vec3 uCamPos;
  uniform float uFogDensity;
  uniform float uSimple;       // tier 0: cheaper lighting
  uniform float uWire;

  uniform float uSafeAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;

  varying vec2 vUv;
  varying vec3 vLocal;
  varying vec3 vWorld;
  varying vec4 vClip;

  float hash12(vec2 p) {
    vec3 p3 = fract(vec3(p.xyx) * 0.1031);
    p3 += dot(p3, p3.yzx + 33.33);
    return fract((p3.x + p3.y) * p3.z);
  }
  float vnoise(vec2 p) {
    vec2 i = floor(p), f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    return mix(mix(hash12(i), hash12(i + vec2(1.0, 0.0)), f.x),
               mix(hash12(i + vec2(0.0, 1.0)), hash12(i + vec2(1.0, 1.0)), f.x), f.y);
  }

  ${OPENING_SDF_CHUNK}

  // ---------- settings ----------
  vec3 settingGallery(vec2 uv, vec3 base) {
    // Dark museum wall, two or three framed covers of different sizes, each
    // under its own spot.
    vec3 col = base * 0.10;
    for (int i = 0; i < 3; i++) {
      float fi = float(i);
      float w = mix(0.14, 0.26, hash12(vec2(uSeed + fi * 7.3, uWall)));
      vec2 c = vec2(0.18 + 0.32 * fi + 0.06 * hash12(vec2(fi, uSeed)), 0.42 + 0.16 * hash12(vec2(uSeed * 3.1, fi)));
      vec2 d = abs(uv - c);
      float inFrame = step(max(d.x - w, d.y - w * 1.15), 0.0);
      float inArt = step(max(d.x - w * 0.88, d.y - w * 1.03), 0.0);
      vec2 auv = (uv - c) / (w * 1.76) + 0.5;
      vec3 art = uHasArt > 0.5 ? texture2D(uArt, auv).rgb : uTint2;
      // the spot: a soft cone falling from above the frame
      float spot = smoothstep(0.55, 0.0, length((uv - (c + vec2(0.0, w * 1.3))) * vec2(1.0, 0.55))) * 0.55;
      col += vec3(0.02) * inFrame + spot * uLightCol * 0.6;
      col = mix(col, art * (0.75 + 0.45 * spot), inArt);
    }
    return col;
  }

  vec3 settingMirror(vec2 uv, vec3 base) {
    // Infinity's lattice-of-copies idea, flattened onto one wall: nested frames
    // receding to a vanishing point, each one dimmer and greener than the last.
    // Reads as infinite depth sideways for the cost of a few rectangles.
    vec3 col = base * 0.04;
    vec2 vp = vec2(0.5 + 0.12 * (hash12(vec2(uSeed, 1.0)) - 0.5), 0.5);
    vec2 d = uv - vp;
    for (int r = 1; r <= 7; r++) {
      float fr = float(r);
      float s = pow(0.72, fr);
      vec2 a = abs(d) / s;
      float rect = max(a.x - 0.46, a.y - 0.42);
      float edge = smoothstep(0.012 / s, 0.0, abs(rect));
      float att = pow(0.88, fr);
      vec3 tint = mix(vec3(1.0), vec3(0.88, 1.0, 0.92), fr * 0.06);
      col += uLightCol * edge * att * tint * (0.8 + 0.6 * uHigh);
      // a dim copy of the cover in each mirror-frame
      if (rect < 0.0 && uHasArt > 0.5) {
        vec2 auv = d / s * 1.05 + 0.5;
        col += texture2D(uArt, auv).rgb * att * 0.08 * tint;
      }
    }
    return col;
  }

  vec3 settingLattice(vec2 uv, vec3 base) {
    // A grid of light with the dark beyond showing between the lines. Stays
    // opaque - occlusion is what makes the rooms read - so "the void beyond" is
    // a very dark field with a few faint stars, not transparency.
    vec3 col = vec3(0.004, 0.005, 0.008);
    vec2 g = fract(uv * 8.0);
    float line = smoothstep(0.045, 0.0, min(g.x, 1.0 - g.x)) + smoothstep(0.045, 0.0, min(g.y, 1.0 - g.y));
    float pulse = 0.65 + 0.35 * sin(uTime * 1.3 + uv.x * 6.0 + uv.y * 4.0 + uSeed);
    col += uLightCol * min(line, 1.0) * (0.55 + 0.7 * uHigh) * pulse;
    float star = smoothstep(0.996, 1.0, hash12(floor(uv * 60.0) + uSeed));
    col += vec3(star) * 0.25;
    return col;
  }

  vec3 settingDrape(vec2 uv, vec3 base) {
    // Cloth with the cover printed on it. A static height field of folds - the
    // print is displaced by it and lit by its slope. No simulation needed for a
    // drape that only has to hang.
    float f1 = sin(uv.x * 9.0 + uSeed) * 0.5 + sin(uv.x * 23.0 + uSeed * 2.0 + uv.y * 3.0) * 0.25;
    float f2 = sin(uv.x * 9.0 + uSeed + 0.05) * 0.5 + sin((uv.x + 0.005) * 23.0 + uSeed * 2.0 + uv.y * 3.0) * 0.25;
    float slope = (f2 - f1) * 40.0;
    vec2 puv = uv + vec2(f1 * 0.012, 0.0);
    vec3 art = uHasArt > 0.5 ? texture2D(uArt, puv).rgb : uTint2;
    float shade = 0.62 + 0.38 * clamp(0.5 + slope * 0.9, 0.0, 1.0);
    return art * shade * (0.55 + 0.45 * base);
  }

  vec3 settingTile(vec2 uv, vec3 base) {
    // Glossy tiles carrying fragments of the cover, grout between them, a
    // highlight sliding across with the high band. Hammam, pool.
    vec2 g = uv * 10.0;
    vec2 cell = floor(g), f = fract(g);
    float grout = smoothstep(0.0, 0.06, min(min(f.x, 1.0 - f.x), min(f.y, 1.0 - f.y)));
    // each tile shows a different fragment of the cover
    vec2 pick = vec2(hash12(cell + uSeed), hash12(cell * 1.7 + uSeed));
    vec2 auv = pick * 0.75 + f * 0.25;
    vec3 art = uHasArt > 0.5 ? texture2D(uArt, auv).rgb : uTint2;
    float gloss = pow(1.0 - abs(f.x + f.y - 1.0 - 0.3 * sin(uTime * 0.7 + cell.x)), 8.0);
    vec3 col = mix(vec3(0.02, 0.02, 0.025), art * 0.85 + uLightCol * gloss * (0.3 + 0.7 * uHigh), grout);
    return col;
  }

  void main() {
    // Far wall: the opening is cut here, in the shader, which is what lets its
    // shape change per room with no geometry work at all.
    float sdf = 0.0;
    if (uWall > 3.5) {
      sdf = openingShape(vLocal.xy - uOpenCentre, vec2(uOpenHalf * uOpenAspect, uOpenHalf));
      if (sdf < 0.0) discard;
    }

    vec3 col;
    vec3 base = uTint;
    if (uType < 0.5) col = settingGallery(vUv, base);
    else if (uType < 1.5) col = settingMirror(vUv, base);
    else if (uType < 2.5) col = settingLattice(vUv, base);
    else if (uType < 3.5) col = settingDrape(vUv, base);
    else if (uType < 4.5) col = vec3(0.0);           // void: only the frame survives below
    else col = settingTile(vUv, base);

    // ---------- this room's light ----------
    vec3 n;
    if (uWall < 0.5) n = vec3(1.0, 0.0, 0.0);
    else if (uWall < 1.5) n = vec3(-1.0, 0.0, 0.0);
    else if (uWall < 2.5) n = vec3(0.0, 1.0, 0.0);
    else if (uWall < 3.5) n = vec3(0.0, -1.0, 0.0);
    else n = vec3(0.0, 0.0, 1.0);
    vec3 toL = uLightPos - vLocal;
    float dist = length(toL);
    float lam = max(dot(n, toL / max(dist, 0.001)), 0.0);
    float fall = 1.0 / (1.0 + dist * dist * 0.02);
    float lit = uSimple > 0.5 ? (0.35 + 0.65 * fall) : (0.18 + lam * fall * 1.6);
    col *= (0.35 + 0.65 * lit) * (0.6 + 0.4 * uLightAmt);
    col += uLightCol * lit * 0.10 * uLightAmt;

    // ---------- the opening frame, and the next room's light on it ----------
    if (uWall > 3.5) {
      // Not a glowing outline any more but an actual picture-frame MOULDING:
      // the passage is a framed artwork, so its edge has to be a moulding with
      // a profile - inner lip, groove, outer lip - catching the room's light.
      // That profile is the whole reason the thing reads as a frame hanging on
      // a wall rather than as a hole with a rim light on it.
      float t = clamp(sdf / uMouldW, 0.0, 1.0);
      float inMould = step(sdf, uMouldW) * step(0.0, sdf);
      float lip = smoothstep(0.14, 0.0, t) + 0.8 * smoothstep(0.82, 1.0, t);
      float groove = 1.0 - smoothstep(0.22, 0.55, abs(t - 0.45));
      vec3 gilt = mix(vec3(0.30, 0.25, 0.17), vec3(0.88, 0.78, 0.56), min(lip, 1.0));
      gilt *= 1.0 - 0.5 * groove;
      col = mix(col, gilt * (0.30 + 0.95 * lit) + uLightCol * min(lip, 1.0) * 0.22, inMould);
      // The next room's light spilling past the frame, and the frame itself
      // lighting up in the two beats before the crossing.
      float rim = smoothstep(2.0, 0.0, sdf);
      col += uNextLight * rim * 0.55;
      // Capped: at the landing flare's peak an uncapped term washed the gilt
      // out to flat white and the moulding stopped reading as a material at
      // all. The flash is the point, the frame going plastic is not.
      col += mix(uLightCol, vec3(1.0), 0.45) * inMould * min(lip, 1.0)
           * min(0.10 + 1.15 * uFrameGlow, 1.55);
    }
    if (uType > 3.5 && uType < 4.5 && uWall > 3.5) {
      // Void: nothing but the frame floats in the dark.
      // Void: nothing but the framed artwork, floating in the dark.
      float t2 = clamp(sdf / uMouldW, 0.0, 1.0);
      float inM = step(sdf, uMouldW) * step(0.0, sdf);
      float lip2 = smoothstep(0.14, 0.0, t2) + 0.8 * smoothstep(0.82, 1.0, t2);
      vec3 gilt2 = mix(vec3(0.24, 0.20, 0.14), vec3(0.90, 0.80, 0.58), min(lip2, 1.0));
      col = gilt2 * inM * (0.45 + 1.3 * uFrameGlow)
          + uLightCol * inM * min(lip2, 1.0) * 0.3
          + uNextLight * smoothstep(2.0, 0.0, sdf) * 0.5;
    }

    // ---------- fog by distance, in the current room's colour ----------
    float dcam = length(vWorld - uCamPos);
    float fog = 1.0 - exp(-uFogDensity * uFogDensity * dcam * dcam);
    col = mix(col, uFogColor, fog);

    // ---------- safe zone ----------
    vec2 ndc = vClip.xy / max(vClip.w, 0.0001);
    vec2 sp = vec2(ndc.x * uSafeAspect, ndc.y);
    vec2 sq = abs(sp - uSafeCenter) - uSafeHalfSize;
    float sd = length(max(sq, 0.0)) + min(max(sq.x, sq.y), 0.0);
    col *= mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, sd));

    if (uWire > 0.5) {
      vec2 e = min(vUv, 1.0 - vUv);
      float ed = smoothstep(0.01, 0.0, min(e.x, e.y));
      col = mix(col, vec3(1.0, 0.3, 0.3), ed);
      if (uWall > 3.5) col = mix(col, vec3(0.3, 1.0, 0.3), smoothstep(0.15, 0.0, abs(sdf)));
    }

    col += (hash12(gl_FragCoord.xy + uTime) - 0.5) / 255.0;
    gl_FragColor = vec4(max(col, 0.0), 1.0);
  }
`;

// THE CANVAS: the cover, hanging in the frame, as the thing you pass through.
// A separate transparent quad rather than part of the wall, and that separation
// is deliberate: the walls stay opaque with depthWrite so a room genuinely
// occludes the ones behind it, and only this veil blends. It is masked by the
// SAME outline the wall is cut by, so the painting can never spill outside its
// own moulding whatever shape that moulding is.
const canvasVertexShader = `
  precision mediump float;
  varying vec2 vUv;
  varying vec3 vWorld;
  varying vec4 vClip;
  void main() {
    vUv = uv;
    vec4 w = modelMatrix * vec4(position, 1.0);
    vWorld = w.xyz;
    vClip = projectionMatrix * viewMatrix * w;
    gl_Position = vClip;
  }
`;

const canvasFragmentShader = `
  precision mediump float;
  uniform sampler2D uArt;
  uniform float uHasArt;
  uniform float uOpenHalf;
  uniform float uOpenAspect;
  uniform vec2 uSize;         // the quad's own extents in room-local units
  uniform float uFade;        // 0 fully open, 1 fully painted
  uniform vec3 uTint;
  uniform vec3 uLightCol;
  uniform float uLightAmt;
  uniform float uTime;
  uniform vec3 uFogColor;
  uniform vec3 uCamPos;
  uniform float uFogDensity;
  uniform float uSafeAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  varying vec2 vUv;
  varying vec3 vWorld;
  varying vec4 vClip;

  ${OPENING_SDF_CHUNK}

  void main() {
    if (uFade <= 0.001) discard;
    vec2 q = (vUv - 0.5) * uSize;
    vec2 openH = vec2(uOpenHalf * uOpenAspect, uOpenHalf);
    float sdf = openingShape(q, openH);
    if (sdf > 0.0) discard;                 // outside the frame's aperture

    // The print fills the aperture EXACTLY - which, the aperture being solved
    // from the cover, is the same thing as saying the print is at the cover's
    // own size. So the painting the cover lands on is the cover.
    vec2 auv = q / (openH * 2.0) + 0.5;
    vec3 art = uHasArt > 0.5 ? texture2D(uArt, auv).rgb : uTint;

    // Varnish: a broad sheen across the surface, which is what stops it from
    // reading as a flat decal and says "there is a physical picture here".
    float sheen = pow(clamp(1.0 - abs(q.x * 0.6 + q.y * 0.35) / (uOpenHalf * 1.6), 0.0, 1.0), 6.0);
    // Lit like a hung picture, not like a wall. At the old 0.55 base the print
    // came out darker than the room around it, which read as a dim hole rather
    // than as a painting with a light on it - and the point of the whole
    // gesture is that you are about to step INTO the picture.
    vec3 col = art * (1.05 + 0.6 * uLightAmt) + uLightCol * sheen * 0.30;

    // It dissolves from the middle outward, so the frame's inner edge is the
    // last thing to let go - the picture opens like an aperture rather than
    // simply going transparent all over.
    float r = clamp(-sdf / max(uOpenHalf, 0.001), 0.0, 1.0);
    float a = uFade * smoothstep(0.0, 0.55, 1.0 - (1.0 - uFade) * 1.35 + r * 0.6);

    float dcam = length(vWorld - uCamPos);
    float fog = 1.0 - exp(-uFogDensity * uFogDensity * dcam * dcam);
    col = mix(col, uFogColor, fog);

    vec2 ndc = vClip.xy / max(vClip.w, 0.0001);
    vec2 sp = vec2(ndc.x * uSafeAspect, ndc.y);
    vec2 sq = abs(sp - uSafeCenter) - uSafeHalfSize;
    float sd = length(max(sq, 0.0)) + min(max(sq.x, sq.y), 0.0);
    col *= mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, sd));

    gl_FragColor = vec4(col, clamp(a, 0.0, 1.0));
  }
`;

// A field of stars around the camera, always there, only really seen when a
// VOID room opens onto it.
const starVertexShader = `
  precision mediump float;
  attribute vec3 aSeed;
  uniform vec3 uCamPos;
  uniform float uDprScale;
  varying float vA;
  void main() {
    // Wrapped around the camera along z so the field never runs out.
    vec3 p = aSeed;
    p.z = uCamPos.z - 10.0 - mod(uCamPos.z - aSeed.z + 200.0, 90.0);
    vec4 mv = modelViewMatrix * vec4(p, 1.0);
    vA = 0.35 + 0.65 * fract(aSeed.x * 13.7);
    gl_Position = projectionMatrix * mv;
    gl_PointSize = clamp((1.0 + fract(aSeed.y * 7.1) * 1.5) * uDprScale, 1.0, 3.0);
  }
`;
const starFragmentShader = `
  precision mediump float;
  varying float vA;
  void main() {
    vec2 c = gl_PointCoord - 0.5;
    if (length(c) > 0.5) discard;
    gl_FragColor = vec4(vec3(0.7, 0.75, 0.9) * vA * 0.7, 1.0);
  }
`;

export class PortalsVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    this._tier = TIER[tier] ? tier : DEFAULT_TIER;
    this._layout = TIER[this._tier];

    const t = urlParam('portalroom');
    this._forcedType = TYPE_ID[t] != null ? t : null;
    const sp = parseFloat(urlParam('portalspeed'));
    this._forcedSpeed = Number.isFinite(sp) ? sp : null;    // beats per second of travel
    this._forceCross = urlParam('portalcross') === '1';
    this._wire = urlParam('portalwire') === '1';
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    this._time = 0;
    this._paused = false;
    this._travel = 0;                 // in BEATS; room k spans [8k, 8k+8)
    this._lastBeatTime = null;
    this._dropUntilBeat = -1;
    this._lastPaletteRef = undefined;
    this._lastArtworkUrl = null;
    this._artTexture = null;
    this._vivid = FALLBACK_VIVID.slice();
    this._fogColor = new THREE.Color(0x06070b);
    this._fogTarget = new THREE.Color();
    this._fogNext = new THREE.Color();
    this._camPos = new THREE.Vector3();
    this._aim = new THREE.Vector3();
    // Per-session, so the weave is not identical every time the visualizer
    // starts, while staying a pure function of the index within one session.
    this._pathSeed = Math.random() * 1000;
    this._openHalf = 1.1;      // replaced by the solve in _syncOpeningToSafeZone
    this._openAspect = 1;
    // Where the view must AIM for the (centred) opening to project onto the
    // cover. Registering by aiming rather than by moving the hole off-centre
    // keeps the camera going through the aperture's middle, so the clearance
    // no longer shrinks as the aperture does.
    this._aimOffset = new THREE.Vector2();
    this._lastWritten = null;                 // for the drift cancellation
    this._residual = new THREE.Vector3();
    this._lastSafeH = -1; this._lastSafeY = -99;
    this._land = 0;            // the landing flare
    this._lastLandRoom = -9999;
    this._lastVoidAt = -10;
    this._roomsBuilt = 0;

    this._buildStars();
    this._buildRooms();
    // Seed the ring: room k at index k, no two alike, one mirror at most.
    for (let k = 0; k < this._rooms.length; k++) this._regenerate(this._rooms[k], k, 0.5);
    this._placeRooms();
    this._syncOpeningToSafeZone();   // also sets every canvas's scale
  }

  // --- construction ---------------------------------------------------------

  _buildStars() {
    const n = 500;
    const seed = new Float32Array(n * 3);
    for (let i = 0; i < n; i++) {
      seed[i * 3] = (Math.random() - 0.5) * 90;
      seed[i * 3 + 1] = (Math.random() - 0.5) * 60;
      seed[i * 3 + 2] = Math.random() * 90;
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(n * 3), 3));
    geo.setAttribute('aSeed', new THREE.BufferAttribute(seed, 3));
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 0, 0), 1e4);
    this._starUniforms = { uCamPos: { value: this._camPos }, uDprScale: { value: 1 } };
    this._starMat = new THREE.ShaderMaterial({
      uniforms: this._starUniforms, vertexShader: starVertexShader, fragmentShader: starFragmentShader,
      transparent: true, depthWrite: false, blending: THREE.AdditiveBlending
    });
    this._starGeo = geo;
    this._stars = new THREE.Points(geo, this._starMat);
    this._stars.frustumCulled = false;
    this._stars.renderOrder = -900;
    this._scene.add(this._stars);
  }

  // One geometry per wall orientation, shared by every room; five materials
  // per room, each knowing which wall it is. Regenerating a room touches
  // uniforms and transforms only - criterion 8.
  _buildRooms() {
    const S = ROOM_SIZE, h = S / 2;
    this._wallGeo = new THREE.PlaneGeometry(S, S);
    // Unit quad, scaled per room to the aperture - so a resize changes a scale
    // and never rebuilds geometry.
    this._canvasGeo = new THREE.PlaneGeometry(1, 1);
    // wall placement in room-local space: [position, rotation]
    const place = [
      [[-h, 0, -h], [0, Math.PI / 2, 0]],      // left, faces +x
      [[h, 0, -h], [0, -Math.PI / 2, 0]],      // right, faces -x
      [[0, -h, -h], [-Math.PI / 2, 0, 0]],     // floor, faces +y
      [[0, h, -h], [Math.PI / 2, 0, 0]],       // ceiling, faces -y
      [[0, 0, -S], [0, 0, 0]]                  // far wall, faces +z (toward the camera)
    ];
    this._rooms = [];
    for (let r = 0; r < this._layout.rooms; r++) {
      const group = new THREE.Group();
      const mats = [], walls = [];
      for (let w = 0; w < 5; w++) {
        const uniforms = {
          uWall: { value: w }, uType: { value: 0 }, uShape: { value: 0 },
          uOpenCentre: { value: new THREE.Vector2() }, uOpenHalf: { value: this._openHalf },
          uOpenAspect: { value: 1 }, uMouldW: { value: this._openHalf * MOULD_FRAC },
          uTint: { value: new THREE.Color() }, uTint2: { value: new THREE.Color() },
          uLightCol: { value: new THREE.Color() }, uLightPos: { value: new THREE.Vector3(0, 2, -6) },
          uLightAmt: { value: 1 }, uNextLight: { value: new THREE.Color() },
          uFrameGlow: { value: 0 }, uHigh: { value: 0 }, uSeed: { value: 0 }, uTime: { value: 0 },
          uArt: { value: null }, uHasArt: { value: 0 },
          uFogColor: { value: this._fogColor }, uCamPos: { value: this._camPos },
          uFogDensity: { value: this._layout.fog ? FOG_DENSITY : 0 },
          uSimple: { value: this._layout.simpleLight ? 1 : 0 }, uWire: { value: this._wire ? 1 : 0 },
          uSafeAspect: safeZoneUniforms.uAspect, uSafeCenter: safeZoneUniforms.uSafeCenter,
          uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather
        };
        const mat = new THREE.ShaderMaterial({
          uniforms: uniforms, vertexShader: wallVertexShader, fragmentShader: wallFragmentShader,
          // Opaque and depth-written: a wall has to really hide what is behind
          // it. Occlusion is one of the three things that make this a journey.
          transparent: false, depthWrite: true, depthTest: true, side: THREE.FrontSide
        });
        const mesh = new THREE.Mesh(this._wallGeo, mat);
        mesh.position.set(place[w][0][0], place[w][0][1], place[w][0][2]);
        mesh.rotation.set(place[w][1][0], place[w][1][1], place[w][1][2]);
        mesh.frustumCulled = false;
        group.add(mesh);
        mats.push(mat); walls.push(mesh);
      }
      // The canvas: one per room, pre-allocated like the walls, so recycling a
      // room never allocates.
      const cu = {
        uArt: { value: null }, uHasArt: { value: 0 },
        uOpenHalf: { value: this._openHalf }, uOpenAspect: { value: 1 },
        uSize: { value: new THREE.Vector2() }, uFade: { value: CANVAS_PEAK },
        uTint: { value: new THREE.Color() }, uLightCol: { value: new THREE.Color() },
        uLightAmt: { value: 1 }, uTime: { value: 0 },
        uFogColor: { value: this._fogColor }, uCamPos: { value: this._camPos },
        uFogDensity: { value: this._layout.fog ? FOG_DENSITY : 0 },
        uSafeAspect: safeZoneUniforms.uAspect, uSafeCenter: safeZoneUniforms.uSafeCenter,
        uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather
      };
      const cmat = new THREE.ShaderMaterial({
        uniforms: cu, vertexShader: canvasVertexShader, fragmentShader: canvasFragmentShader,
        // The one blended element. depthTest stays on so a nearer wall still
        // hides it; depthWrite is off so it does not occlude what is beyond.
        transparent: true, depthTest: true, depthWrite: false, side: THREE.FrontSide
      });
      const canvas = new THREE.Mesh(this._canvasGeo, cmat);
      canvas.frustumCulled = false;
      canvas.renderOrder = 10;
      group.add(canvas);

      this._scene.add(group);
      this._rooms.push({
        group: group, mats: mats, walls: walls,
        index: r, type: 'gallery', shape: 0,
        canvas: canvas, canvasMat: cmat, canvasUniforms: cu,
        open: new THREE.Vector2(), tint: new THREE.Color(), light: new THREE.Color(),
        lightPos: new THREE.Vector3(), lightBase: 1, seed: 0
      });
    }
  }

  // --- the ring -------------------------------------------------------------

  // Room k's threshold is at z = -k*SPACING, its far wall (the opening) at
  // -(k+1)*SPACING. The walls themselves start DOOR_DEPTH past the threshold.
  _placeRooms() {
    for (const room of this._rooms) {
      room.group.position.set(0, 0, -room.index * SPACING - DOOR_DEPTH);
    }
  }

  _pickType(index, progress) {
    if (this._forcedType) return this._forcedType;
    const allowed = this._layout.types;
    const prevRoom = this._roomAt(index - 1);
    const prevType = prevRoom ? prevRoom.type : null;
    const mirrorLive = this._rooms.some((r) => r.type === 'mirror' && r.index !== index);
    const arc = Math.sin(Math.PI * clamp01(progress));
    // progress -> the calm settings at the ends of a track, the dense ones in
    // the middle.
    const weight = (t) => {
      let w = 1;
      if (t === 'void') w = (index - this._lastVoidAt >= 4) ? 0.9 : 0.0;   // about one in five
      if (t === 'drape') w = 1.2 - 0.6 * arc;
      if (t === 'mirror' || t === 'tile') w = 0.5 + 0.9 * arc;
      if (t === prevType) w = 0;                                            // criterion 6
      if (t === 'mirror' && mirrorLive) w = 0;                              // criterion 7
      if (allowed.indexOf(t) < 0) w = 0;
      return w;
    };
    let total = 0;
    const ws = TYPES.map((t) => { const w = weight(t); total += w; return w; });
    if (total <= 0) return allowed.find((t) => t !== prevType) || allowed[0];
    let r = Math.random() * total;
    for (let i = 0; i < TYPES.length; i++) { r -= ws[i]; if (r <= 0) return TYPES[i]; }
    return TYPES[TYPES.length - 1];
  }

  // The serpentine used to live in the WALLS - each opening offset a little
  // from the last. That is incompatible with the cover landing in the frame:
  // an off-centre opening does not project to the centre of the screen, and
  // the centre of the screen is exactly where the DOM cover is. So the weave
  // moved into the CAMERA. The openings are all centred now, and the camera
  // wanders inside each room instead.
  //
  // Still a DETERMINISTIC function of the room index, not a random draw at
  // regeneration time - that is what lets the path be evaluated at any travel
  // value at all, including for the room just left (already recycled) and the
  // one two ahead (not yet placed), which is what killed the snap at each
  // crossing. Returned as separate numbers so two rooms can be read at once
  // without a scratch vector to alias.
  _swayX(index) {
    const h = Math.sin(index * 127.1 + this._pathSeed) * 43758.5453;
    const a = h - Math.floor(h);
    const g = Math.sin(index * 311.7 + this._pathSeed * 1.7) * 24634.6345;
    return lerp(OFFSET_MIN, OFFSET_MAX, a) * ROOM_SIZE * ((g - Math.floor(g)) < 0.5 ? -1 : 1);
  }
  _swayY(index) {
    const h = Math.sin(index * 74.7 + this._pathSeed * 2.3) * 43758.5453;
    const a = h - Math.floor(h);
    const g = Math.sin(index * 197.3 + this._pathSeed * 3.1) * 24634.6345;
    return lerp(OFFSET_MIN, OFFSET_MAX, a) * ROOM_SIZE * 0.7 * ((g - Math.floor(g)) < 0.5 ? -1 : 1);
  }

  // The path, as one continuous curve. The wander is a bulge WITHIN each room
  // that is back to nothing before the landing: sin^2 is zero in value AND in
  // slope at both ends of its window, so the camera rejoins the axis smoothly
  // instead of kinking at the door, and from the landing to the crossing it is
  // exactly on the axis - which is what makes the cover and the frame's
  // content register. Both endpoints of the camera's frame, where it is and
  // where it aims, are read off this same function, so neither can jump when
  // the room index ticks over.
  _pathAt(travelBeats, out) {
    const k = Math.floor(travelBeats / BEATS_PER_ROOM);
    const phase = travelBeats / BEATS_PER_ROOM - k;
    const u = (phase - SWAY_START_PHASE) / (SWAY_END_PHASE - SWAY_START_PHASE);
    const env = (u <= 0 || u >= 1) ? 0 : Math.pow(Math.sin(Math.PI * u), 2);
    out.set(
      this._swayX(k) * env,
      this._swayY(k) * env * 0.6,
      -travelBeats / BEATS_PER_ROOM * SPACING
    );
    return out;
  }

  _roomAt(index) {
    for (const r of this._rooms) if (r.index === index) return r;
    return null;
  }

  // Give a room a new life as ring slot `index`. Only parameter writes here.
  _regenerate(room, index, progress) {
    room.index = index;
    room.type = this._pickType(index, progress);
    if (room.type === 'void') this._lastVoidAt = index;
    room.shape = (index * 2 + ((Math.random() * SHAPES) | 0)) % SHAPES;
    room.seed = Math.random() * 100;
    // Centred: the aim, not the hole, is what puts it on the cover.
    room.open.set(0, 0);
    // colours: a different dominant per room
    const v = this._vivid;
    const c1 = v[(index * 2 + 1) % v.length], c2 = v[(index * 3 + 2) % v.length];
    room.tint.set(c1);
    room.light.set(c1).lerp(new THREE.Color(0xffffff), 0.35);
    room.lightBase = lerp(0.7, 1.3, Math.random());
    room.lightPos.set((Math.random() - 0.5) * 6, 2.5 + Math.random() * 2, -3 - Math.random() * 6);
    for (let w = 0; w < 5; w++) {
      const u = room.mats[w].uniforms;
      u.uType.value = TYPE_ID[room.type];
      u.uShape.value = room.shape;
      u.uOpenCentre.value.copy(room.open);
      u.uTint.value.copy(room.tint);
      u.uTint2.value.set(c2);
      u.uLightCol.value.copy(room.light);
      u.uLightPos.value.copy(room.lightPos);
      u.uSeed.value = room.seed;
      // The variety the five aperture outlines used to carry now lives here:
      // every room's frame is a different weight of moulding.
      u.uMouldW.value = this._openHalf * MOULD_FRAC * (0.72 + 0.17 * room.shape);
      // Void: the four side walls simply are not there.
      room.walls[w].visible = !(room.type === 'void' && w < 4);
    }
    const cu = room.canvasUniforms;
    cu.uTint.value.copy(room.tint);
    cu.uLightCol.value.copy(room.light);
    room.canvas.position.set(room.open.x, room.open.y, -ROOM_SIZE + 0.02);
    this._roomsBuilt++;
  }

  // The aperture is SOLVED, not chosen: at the landing distance it has to
  // subtend exactly the DOM cover, so that the frame's content and the cover
  // are the same size on screen at that moment.
  //
  // Measured from #overlay-artwork - the artwork element ALONE. #overlay's own
  // rect, which is what the safe zone is built from, also contains the title
  // and artist lines, and sizing the frame from it would fit the frame to the
  // text as well as to the picture. The conversion goes through the safe-zone
  // half-height as a ratio of the two rects rather than through
  // window.innerHeight, so it stays anchored to the same measurement
  // scene-manager made and cannot drift if the canvas is not the window size.
  _syncOpeningToSafeZone() {
    const cam = this._camera;
    const fov = (cam.fov || 68) * Math.PI / 180;
    const dLand = SPACING * LANDING_BEATS / BEATS_PER_ROOM;
    const frameHalfAtLanding = dLand * Math.tan(fov / 2);   // world half-height there
    // R19: sized and placed from the overlay block AS IT WOULD BE at the centre
    // of composition (uStage*), not where the cover is - the corridor does not
    // follow the cover. Only the artwork's offset WITHIN the block is measured.
    let halfH = safeZoneUniforms.uStageHalfSize.value.y;
    let aspect = 1, offX = 0, offY = 0;
    if (typeof document !== 'undefined') {
      const ov = document.getElementById('overlay');
      const ar = document.getElementById('overlay-artwork');
      const ovr = ov && ov.getBoundingClientRect();
      const arr = ar && ar.getBoundingClientRect();
      if (ovr && arr && ovr.height > 1 && arr.height > 1) {
        // px -> the safe zone's own height units, via the ratio of the two
        // rects, so no viewport size is assumed anywhere.
        const s2 = 2 * safeZoneUniforms.uStageHalfSize.value.y / ovr.height;
        halfH = (arr.height / 2) * s2;
        aspect = arr.width / arr.height;
        // The artwork is NOT at the centre of the viewport: the title and
        // artist lines below it inside #overlay push the picture above the
        // block's own midpoint - the same fact pulse-core.js records as its
        // ARTWORK_CENTER_OFFSET. So the opening is offset on the wall by the
        // world equivalent of the cover's real screen offset, and projects
        // onto the cover instead of onto the middle of the screen. Shifting
        // the content, not the view.
        const dx = (arr.left + arr.width / 2) - (ovr.left + ovr.width / 2);
        const dy = (arr.top + arr.height / 2) - (ovr.top + ovr.height / 2);
        offX = safeZoneUniforms.uStageCenter.value.x + dx * s2;   // height units
        offY = safeZoneUniforms.uStageCenter.value.y - dy * s2;
      }
    }
    this._openHalf = Math.max(0.3, halfH * frameHalfAtLanding);
    this._openAspect = Math.max(0.3, Math.min(3, aspect));
    // A point straight ahead appears off-centre by exactly as much as the view
    // is aimed away from it, so aiming the lookahead target DOWN by the cover's
    // own screen offset puts the centred opening onto the cover. No clamp is
    // needed any more - the hole stays on the axis the camera flies along.
    const lookDist = SPACING * LOOKAHEAD_BEATS / BEATS_PER_ROOM;
    const tanHalf = Math.tan(fov / 2);
    this._aimOffset.set(-offX * tanHalf * lookDist, -offY * tanHalf * lookDist);
    const qw = this._openHalf * this._openAspect * CANVAS_QUAD;
    const qh = this._openHalf * CANVAS_QUAD;
    for (const room of this._rooms) {
      for (const m of room.mats) {
        m.uniforms.uOpenHalf.value = this._openHalf;
        m.uniforms.uOpenAspect.value = this._openAspect;
      }
      room.canvasUniforms.uOpenHalf.value = this._openHalf;
      room.canvasUniforms.uOpenAspect.value = this._openAspect;
      room.canvasUniforms.uSize.value.set(qw, qh);
      room.canvas.scale.set(qw, qh, 1);
      for (const m of room.mats) {
        m.uniforms.uOpenCentre.value.set(0, 0);
        m.uniforms.uMouldW.value = this._openHalf * MOULD_FRAC * (0.72 + 0.17 * room.shape);
      }
      room.open.set(0, 0);
      room.canvas.position.set(0, 0, -ROOM_SIZE + 0.02);
    }
  }

  // --- frame ----------------------------------------------------------------

  update(deltaTime, profile) {
    const paused = profile.playbackState !== 'PLAYING';
    this._paused = paused;
    if (paused) return;   // gel de l'avance

    const dt = Math.min(deltaTime, 0.05);
    this._time += dt;

    const director = profile.director;
    const bands = director && director.bands;
    const has = !!(bands && director.hasReliableGrid);
    const low = has ? clamp01(bands.low) : 0.5 + 0.3 * Math.sin(this._time * 0.4);
    const high = has ? clamp01(bands.high) : 0.4 + 0.3 * Math.sin(this._time * 0.7 + 1.0);
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || FALLBACK_BPM;

    // ---- travel, in beats --------------------------------------------------
    // Off the grid's own index + phase when there is one, so a crossing lands
    // on a beat boundary EXACTLY - a time-based speed would drift against the
    // grid within a minute. Jumps (track change, seek) are ignored rather than
    // travelled.
    let dBeat;
    if (this._forcedSpeed != null) {
      dBeat = this._forcedSpeed * dt;
    } else if (profile.beatIndex != null && profile.beatPhase != null) {
      const bt = profile.beatIndex + profile.beatPhase;
      dBeat = this._lastBeatTime == null ? 0 : bt - this._lastBeatTime;
      if (!(dBeat >= 0 && dBeat < 4)) dBeat = dt * bpm / 60;
      this._lastBeatTime = bt;
    } else {
      this._lastBeatTime = null;
      dBeat = dt * bpm / 60;
    }
    // A known drop doubles the travel for one bar.
    const next = director && director.nextEvent;
    if (next && next.type === 'drop' && next.score >= DROP_SCORE_MIN && next.msUntil <= 40) {
      this._dropUntilBeat = this._travel + 4;
    }
    const mult = this._travel < this._dropUntilBeat ? DROP_MULT : 1;
    this._travel += dBeat * mult;
    if (this._forceCross) { this._forceCross = false; this._travel = Math.ceil(this._travel / BEATS_PER_ROOM) * BEATS_PER_ROOM - 0.05; }

    // ---- recycle rooms the camera has passed -------------------------------
    const roomK = Math.floor(this._travel / BEATS_PER_ROOM);       // the room we are in
    const progress = profile.duration > 0 ? clamp01(profile.progress) : 0.5;
    for (const room of this._rooms) {
      if (room.index < roomK) {
        // Passed. 4 x 14 units ahead and reborn - beyond the fog, so unseen.
        this._regenerate(room, room.index + this._rooms.length, progress);
        room.group.position.z = -room.index * SPACING - DOOR_DEPTH;
      }
    }

    // ---- camera: one continuous path, position and aim both off it ---------
    // No reframing at a crossing. The aim is simply a point FURTHER DOWN THE
    // SAME CURVE, so the orientation is as continuous as the position is.
    // Aiming at "the current room's opening" - which is what this did before -
    // meant the target changed identity on the frame the room index ticked
    // over, and the view snapped every eight beats.
    const phase = (this._travel - roomK * BEATS_PER_ROOM) / BEATS_PER_ROOM;   // 0..1 across this room
    const cur = this._roomAt(roomK);
    // The overlay can change size without a resize ever firing - a longer
    // title wraps to a second line and moves the artwork - and the whole
    // registration is measured off that rect, so re-solve when it moves
    // instead of only on resize. Compared against the safe-zone uniforms,
    // which the director bus already recomputes live, so this costs two DOM
    // reads when something actually changed and nothing at all otherwise.
    const sCen = safeZoneUniforms.uSafeCenter.value;
    const sHalf = safeZoneUniforms.uSafeHalfSize.value;
    if (Math.abs(sHalf.y - this._lastSafeH) > 0.002 || Math.abs(sCen.y - this._lastSafeY) > 0.002) {
      this._lastSafeH = sHalf.y; this._lastSafeY = sCen.y;
      this._syncOpeningToSafeZone();
    }

    this._pathAt(this._travel, this._camPos);
    // scene-manager adds a slow global drift to the camera AFTER update()
    // (its DRIFT_POSITION_AMOUNT, re-based on whatever the visualizer wrote).
    // Up to 0.22 units, which at the landing distance slides the frame a tenth
    // of the cover's width off the cover - so it is measured and subtracted,
    // the same cancellation infinity.js does and for the same reason. Its
    // ROTATION drift is left alone: under a degree, about 3% of the cover, and
    // it keeps the view from feeling locked to a rail.
    if (this._lastWritten == null) {
      this._lastWritten = this._camPos.clone();
    } else {
      this._residual.copy(this._camera.position).sub(this._lastWritten);
      this._lastWritten.copy(this._camPos).sub(this._residual);
    }
    this._camera.position.copy(this._lastWritten);
    // The aim target is shifted by the SAME residual as the position. lookAt
    // builds the orientation from where the camera currently is, which during
    // the cancellation is the compensated point, not the intended one - aiming
    // at the un-shifted target tilted the view by residual/lookahead and threw
    // the frame a tenth of the cover's width sideways. Shifting both endpoints
    // leaves the direction exactly as intended.
    this._pathAt(this._travel + LOOKAHEAD_BEATS, this._aim).sub(this._residual);
    this._aim.x += this._aimOffset.x;
    this._aim.y += this._aimOffset.y;
    this._camera.lookAt(this._aim);

    // ---- per-room lighting, frame glow, fog ---------------------------------
    const beatPhase = profile.beatPhase != null ? profile.beatPhase : 0;
    const barPhase = profile.barPhase != null ? profile.barPhase : 0;
    const pulse = 1 + LIGHT_PULSE * Math.max(0, 1 - beatPhase * 4);
    const beatsToCross = BEATS_PER_ROOM - (this._travel - roomK * BEATS_PER_ROOM);
    // THE LANDING: the moment the frame's content and the cover are the same
    // size on screen. _travel is monotonic, so this is one threshold per room
    // rather than a per-frame comparison that could fire twice.
    const landTravel = roomK * BEATS_PER_ROOM + BEATS_PER_ROOM - LANDING_BEATS;
    if (this._lastLandRoom !== roomK && this._travel >= landTravel) {
      this._lastLandRoom = roomK;
      this._land = 1;
    }
    this._land *= Math.exp(-LAND_DECAY * dt);
    const frameGlow = clamp01(1 - beatsToCross / FRAME_ANTICIPATE) * 0.85
      + (barPhase < 0.1 ? 0.15 : 0) + this._land * 1.0;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    this._starUniforms.uDprScale.value = dpr;

    for (const room of this._rooms) {
      const nextRoom = this._roomAt(room.index + 1);
      const isCur = room.index === roomK;
      for (let w = 0; w < 5; w++) {
        const u = room.mats[w].uniforms;
        u.uTime.value = this._time;
        u.uHigh.value = high;
        u.uLightAmt.value = room.lightBase * (0.55 + 0.75 * low) * (isCur ? pulse : 1);
        u.uFrameGlow.value = isCur ? frameGlow : 0;
        if (nextRoom) u.uNextLight.value.copy(nextRoom.light).multiplyScalar(nextRoom.lightBase * 0.8);
        else u.uNextLight.value.set(0, 0, 0);
      }
      // The painting only clears for the room being ENTERED. The ones further
      // ahead stay painted, so what you see down the line is a succession of
      // framed covers receding - and each is handed the real cover in turn.
      const cu = room.canvasUniforms;
      cu.uTime.value = this._time;
      cu.uLightAmt.value = room.lightBase * (0.55 + 0.75 * low) * (isCur ? pulse : 1);
      // Fully painted right THROUGH the contact - that is the superposition,
      // and clearing it beforehand (which is what this did) meant the two
      // covers never actually met: an empty frame arrived at the right size
      // instead. It dissolves only afterwards, over the beat and a half in
      // which the picture opens out past the cover and swallows the camera.
      cu.uFade.value = isCur
        ? CANVAS_PEAK * clamp01(beatsToCross / LANDING_BEATS)
        : CANVAS_PEAK;
    }
    // Fog takes the current room's tint and slides to the next one's over the
    // last quarter of the room.
    if (cur) {
      const target = this._fogTarget.copy(cur.tint).multiplyScalar(0.10);
      const after = this._roomAt(roomK + 1);
      if (phase > 0.75 && after) {
        target.lerp(this._fogNext.copy(after.tint).multiplyScalar(0.10), (phase - 0.75) * 4);
      }
      this._fogColor.lerp(target, Math.min(1, dt * 3));
    }

    // ---- palette / artwork ---------------------------------------------------
    if (profile.artworkPalette !== this._lastPaletteRef) {
      this._lastPaletteRef = profile.artworkPalette;
      const pal = profile.artworkPalette;
      this._vivid = (pal && pal.vividColors && pal.vividColors.length) ? pal.vividColors.slice() : FALLBACK_VIVID.slice();
      // Rooms not yet visible take the new palette now; the visible ones keep
      // theirs until they are recycled, so nothing snaps in front of the eye.
      for (const room of this._rooms) if (room.index > roomK + 1) this._regenerate(room, room.index, progress);
    }
    if (profile.artworkUrl !== this._lastArtworkUrl) {
      this._lastArtworkUrl = profile.artworkUrl;
      if (profile.artworkUrl) {
        const self = this;
        loadArtworkTexture(profile.artworkUrl, { wrap: THREE.ClampToEdgeWrapping, mipmaps: true }, function (tex) {
          if (self._artTexture) self._artTexture.dispose();
          self._artTexture = tex;
          for (const room of self._rooms) {
            for (const m of room.mats) { m.uniforms.uArt.value = tex; m.uniforms.uHasArt.value = 1; }
            room.canvasUniforms.uArt.value = tex;
            room.canvasUniforms.uHasArt.value = 1;
          }
        }, function () {});
      }
    }
  }

  dispose() {
    this._scene.remove(this._stars);
    this._starGeo.dispose();
    this._starMat.dispose();
    for (const room of this._rooms) {
      this._scene.remove(room.group);
      for (const m of room.mats) m.dispose();
      room.canvasMat.dispose();
    }
    this._wallGeo.dispose();
    this._canvasGeo.dispose();
    if (this._artTexture) { this._artTexture.dispose(); this._artTexture = null; }
  }
}
